/**
 * LetGoReviewScreen.js
 *
 * FT-282: pre-Let-Go review screen. Master reviews everything the form
 * captured before it becomes a permanent MCA-inspectable record.
 *
 * Renders a non-editable summary of the StartVoyageFullScreen form state.
 * Two CTAs: "Edit" (back to form) and "Confirm and Let Go" (proceeds to
 * FT-280 TimePickerModal → onStart).
 */

import React, { useMemo } from 'react';
import {
  Anchor,
  ChevronLeft,
  MapPin,
  Users,
  Cloud,
  Fuel,
  CheckSquare,
  Check,
  ShieldX,
  Phone,
  Edit3
} from 'lucide-react';
// FT-285 Item 1: inline event-time field owned by the Review screen so the
// "Confirm and Let Go" button can submit a fresh timeOptions snapshot
// instead of opening a second modal.
import InlineTimeField from './InlineTimeField';
import { useInlineEventTime } from './useInlineEventTime';

const SectionHeader = ({ icon: Icon, title, badge }) => (
  <div className="flex items-center justify-between mb-3">
    <div className="flex items-center gap-2">
      <Icon className="h-5 w-5 text-amber-600" />
      <h3 className="font-semibold text-gray-900">{title}</h3>
    </div>
    {badge}
  </div>
);

const LetGoReviewScreen = ({
  formData,
  selectedCrew,
  allCrew,
  selectedMasterId,
  masterDesignation,
  weather,
  fuelData,
  checks,
  requiredChecks,
  passengerBriefingConfirmed,
  overrides,
  vessel,
  vesselTimezone = 'UTC',  // FT-285 Item 1: parent resolves; chain matches the old TimePickerModal mount.
  onEdit,
  onConfirm,
  isSubmitting = false  // FT-285 Item 1: parent passes the inflight flag so Confirm disables during submit.
}) => {
  // FT-285 Item 1: hook instance lives on the Review screen so each mount
  // gets a fresh "(now)" badge. freshSnapshot() re-stamps at submit, so the
  // audit ISO is accurate even if the Master left the Review screen open.
  const timeField = useInlineEventTime(vesselTimezone);
  // Resolve crew names for display
  const crewNames = useMemo(() => {
    return selectedCrew
      .map(id => allCrew.find(c => c.id === id))
      .filter(Boolean)
      .map(c => ({
        id: c.id,
        name: c.name || c.full_name || 'Unknown',
        role: c.role || c.position || '',
        isMaster: c.id === selectedMasterId
      }));
  }, [selectedCrew, allCrew, selectedMasterId]);

  const masterName = masterDesignation?.masterName
    || crewNames.find(c => c.isMaster)?.name
    || 'Not designated';

  const tickedCount = requiredChecks.filter(c => checks[c.id]).length;
  const totalChecks = requiredChecks.length;
  const allTicked = tickedCount === totalChecks;

  const weatherSummary = useMemo(() => {
    const parts = [];
    if (weather.windDirection) parts.push(`Wind ${weather.windDirection}`);
    if (weather.windSpeed) parts.push(`${weather.windSpeed} kt`);
    if (weather.seaState) parts.push(weather.seaState);
    if (weather.visibility) parts.push(`Vis ${weather.visibility}`);
    return parts.length ? parts.join(' · ') : 'Not recorded';
  }, [weather]);

  const fuelLabel = fuelData
    ? (fuelData.percentage != null ? `${fuelData.percentage}%` : (fuelData.value != null ? `${fuelData.value}` : 'Recorded'))
    : 'Not recorded';

  return (
    // pb-24 on mobile clears the FAB / bottom navigation bar so the action row
    // (Edit / Confirm and Let Go) isn't hidden behind it. lg:pb-0 on desktop
    // where the bar doesn't overlay.
    <div className="h-full flex flex-col bg-gray-50 pb-24 lg:pb-0">
      {/* Header */}
      <div className="bg-white border-b border-amber-200 px-4 py-4">
        <div className="flex items-center gap-3">
          <button
            onClick={onEdit}
            className="p-2 -ml-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg"
            aria-label="Back to form"
          >
            <ChevronLeft className="h-6 w-6" />
          </button>
          <div className="flex-1">
            <h1 className="text-xl font-semibold text-gray-900">Review Before Let Go</h1>
            <p className="text-sm text-amber-700">
              This will become a permanent MCA-inspectable record.
            </p>
          </div>
        </div>
      </div>

      {/* Body — scrollable */}
      <div className="flex-1 overflow-auto">
        <div className="p-4 max-w-2xl mx-auto space-y-4">

          {/* Amber heads-up banner (DraftVoyageBanner style) */}
          <div className="bg-amber-50 border-l-4 border-amber-400 px-4 py-3 rounded-md" role="status">
            <p className="text-sm font-medium text-amber-900">
              You are about to commit voyage{vessel?.name ? ` for ${vessel.name}` : ''} to the official record.
            </p>
            <p className="text-xs text-amber-800 mt-1">
              Review every field below. Tap Edit to make changes, or Confirm and Let Go to choose the departure time and submit.
            </p>
          </div>

          {/* Route */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
            <SectionHeader icon={MapPin} title="Route" />
            <dl className="grid grid-cols-3 gap-2 text-sm">
              <dt className="text-gray-500">From</dt>
              <dd className="col-span-2 font-medium text-gray-900">{formData.from || '—'}</dd>
              <dt className="text-gray-500">To</dt>
              <dd className="col-span-2 font-medium text-gray-900">{formData.to || '—'}</dd>
              {formData.purpose && (
                <>
                  <dt className="text-gray-500">Purpose</dt>
                  <dd className="col-span-2 text-gray-900">{formData.purposeOther || formData.purpose}</dd>
                </>
              )}
              {formData.eta && (
                <>
                  <dt className="text-gray-500">ETA</dt>
                  <dd className="col-span-2 text-gray-900">{formData.eta}</dd>
                </>
              )}
            </dl>
          </div>

          {/* Master + Crew */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
            <SectionHeader
              icon={Users}
              title="Master & Crew"
              badge={
                <span className="text-xs px-2 py-0.5 rounded-full bg-amber-100 text-amber-800">
                  {crewNames.length} on board
                </span>
              }
            />
            <div className="mb-3 p-3 bg-amber-50 rounded-md border border-amber-200">
              <p className="text-xs text-amber-700 uppercase tracking-wide">Master</p>
              <p className="text-base font-semibold text-amber-900">{masterName}</p>
              {masterDesignation?.qualifyingCert && (
                <p className="text-xs text-amber-700 mt-1">{masterDesignation.qualifyingCert}</p>
              )}
            </div>
            <ul className="divide-y divide-gray-100">
              {crewNames.map(c => (
                <li key={c.id} className="py-2 flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-900">{c.name}</p>
                    {c.role && <p className="text-xs text-gray-500">{c.role}</p>}
                  </div>
                  {c.isMaster && (
                    <span className="text-xs px-2 py-0.5 rounded-full bg-amber-100 text-amber-700">Master</span>
                  )}
                </li>
              ))}
              {crewNames.length === 0 && (
                <li className="py-2 text-sm text-red-600">No crew selected</li>
              )}
            </ul>
            {formData.passengerCount > 0 && (
              <div className="mt-3 pt-3 border-t border-gray-100">
                <p className="text-sm text-gray-700">
                  Passengers: <span className="font-semibold">{formData.passengerCount}</span>
                </p>
                <p className={`text-xs mt-1 ${passengerBriefingConfirmed ? 'text-green-700' : 'text-red-600'}`}>
                  {passengerBriefingConfirmed
                    ? '✓ Safety briefing completed'
                    : '✗ Safety briefing NOT confirmed'}
                </p>
              </div>
            )}
          </div>

          {/* Weather */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
            <SectionHeader icon={Cloud} title="Departure Weather" />
            <p className="text-sm text-gray-900">{weatherSummary}</p>
          </div>

          {/* Fuel */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
            <SectionHeader icon={Fuel} title="Departure Fuel" />
            <p className="text-sm text-gray-900">{fuelLabel}</p>
          </div>

          {/* Pre-Departure Checks */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
            <SectionHeader
              icon={CheckSquare}
              title="Pre-Departure Checks"
              badge={
                <span className={`text-xs px-2 py-0.5 rounded-full ${
                  allTicked ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'
                }`}>
                  {tickedCount}/{totalChecks}
                </span>
              }
            />
            <ul className="space-y-1">
              {requiredChecks.map(check => (
                <li key={check.id} className="flex items-start gap-2 text-sm">
                  {checks[check.id] ? (
                    <Check className="h-4 w-4 text-green-600 flex-shrink-0 mt-0.5" />
                  ) : (
                    <span className="inline-block h-4 w-4 rounded border border-red-400 flex-shrink-0 mt-0.5" />
                  )}
                  <span className={checks[check.id] ? 'text-green-700' : 'text-red-700'}>
                    {check.text}
                  </span>
                </li>
              ))}
            </ul>
          </div>

          {/* Compliance Overrides — only if any */}
          {overrides.length > 0 && (
            <div className="bg-white rounded-xl shadow-sm border border-red-200 p-4">
              <SectionHeader
                icon={ShieldX}
                title="Compliance Overrides"
                badge={
                  <span className="text-xs px-2 py-0.5 rounded-full bg-red-100 text-red-700">
                    {overrides.length}
                  </span>
                }
              />
              <ul className="space-y-3">
                {overrides.map(o => (
                  <li key={o.id} className="border-l-2 border-red-300 pl-3">
                    <p className="text-sm font-medium text-red-800">{o.blockerName}</p>
                    {o.crewName && <p className="text-xs text-red-700">Crew: {o.crewName}</p>}
                    <p className="text-xs text-gray-700 mt-1">Reason: {o.reason}</p>
                    {o.dispensationRef && (
                      <p className="text-xs text-gray-600">Dispensation: {o.dispensationRef}</p>
                    )}
                    <p className="text-xs text-gray-500 mt-1">Acknowledged by {o.acknowledgedBy}</p>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Shore contact */}
          {formData.shoreNotified && (
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
              <div className="flex items-center gap-2 text-sm text-gray-700">
                <Phone className="h-4 w-4 text-amber-600" />
                Shore contact notified
              </div>
            </div>
          )}

          {/* Pre-departure notes */}
          {formData.preDepartureNotes && (
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
              <p className="text-xs text-gray-500 uppercase tracking-wide mb-2">Pre-Departure Notes</p>
              <p className="text-sm text-gray-900 whitespace-pre-wrap">{formData.preDepartureNotes}</p>
            </div>
          )}

        </div>
      </div>

      {/* FT-285 Item 1: Inline event-time field above the footer. Replaces
          the TimePickerModal step that used to fire after Confirm. */}
      <div className="bg-white border-t border-gray-200 px-4 pt-3 pb-1">
        <div className="max-w-2xl mx-auto">
          <InlineTimeField
            timeField={timeField}
            vesselTimezone={vesselTimezone}
            label="Let Go Time"
          />
        </div>
      </div>

      {/* Footer — Edit + Confirm */}
      <div className="bg-white border-t border-gray-200 p-4">
        <div className="max-w-2xl mx-auto flex gap-3">
          <button
            type="button"
            onClick={onEdit}
            className="flex-1 py-3 px-4 border border-gray-300 text-gray-700 rounded-lg font-medium hover:bg-gray-50 flex items-center justify-center gap-2"
            style={{ minHeight: '56px' }}
          >
            <Edit3 className="h-5 w-5" />
            Edit
          </button>
          <button
            type="button"
            onClick={() => onConfirm(timeField.freshSnapshot())}
            disabled={!timeField.isValid || isSubmitting}
            className="flex-1 py-3 px-4 bg-green-600 text-white rounded-lg font-medium hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            style={{ minHeight: '56px' }}
          >
            <Anchor className="h-5 w-5" />
            Confirm and Let Go
          </button>
        </div>
      </div>
    </div>
  );
};

export default LetGoReviewScreen;
